import {
  addDays,
  eachDayOfInterval,
  endOfWeek,
  isSameDay,
  startOfWeek,
} from 'date-fns';
import { useMemo } from 'react';
import { weekdaysNumberMap } from 'shared/constants';
import { useToday } from 'shared/contexts/today';
import { Task } from 'shared/types/task';
import { getTaskPlanning } from 'shared/utils/get-task-planning';

import { useData } from '../../use-data';

export const useUpcomingWeeks = (task: Task) => {
  const today = useToday();
  const { weekStartsOn } = useData();

  const dateRange = useMemo(() => {
    const dateFnsOptions = { weekStartsOn: weekdaysNumberMap[weekStartsOn] };
    return {
      startDate: startOfWeek(today, dateFnsOptions),
      endDate: addDays(endOfWeek(today, dateFnsOptions), 21),
    };
  }, [today, weekStartsOn]);

  const planning = useMemo(
    () =>
      getTaskPlanning(task, {
        weekStartsOn,
        ...dateRange,
      }),
    [dateRange, task, weekStartsOn],
  );

  return useMemo(() => {
    const days = eachDayOfInterval({
      start: dateRange.startDate,
      end: dateRange.endDate,
    }).map((date) => ({
      date,
      entry: planning.find((entry) => isSameDay(entry.date, date)),
    }));

    // group days into weeks (arrays of 7 days)
    return Array.from({ length: Math.ceil(days.length / 7) }, (_, i) => ({
      date: days[i * 7].date, // First date of the week
      days: days.slice(i * 7, i * 7 + 7), // Week's days with their entries
    }));
  }, [dateRange.endDate, dateRange.startDate, planning]);
};
